import type { QuestionOptionDisplayType } from "@/types/question";

export type ChecklistStartPayload = {
	cpf: string;
	plate: string;
	checklistId: number;
	templateId: number;
};

export type ChecklistStep =
	| "summary"
	| "general"
	| "initialize"
	| "section"
	| "submit"
	| "finish";

export type DamageSummary = {
	None?: number;
	Scratch?: number;
	Chip?: number;
	Dent?: number;
	Broken?: number;
	Missing?: number;
	Crack?: number;
	Stain?: number;
	Other?: number;
};

export type DamageReportFile = {
	fileKey: string;
	fileUrl: string;
	description: string;
	damageSummary: DamageSummary;
};

export const VehicleConditionsType = {
	REGULAR: "Regular",
	DAMAGED: "Avariado",
	NOT_AVAILABLE: "Não possui",
} as const;

export const VehicleConditionsTypeColor: Record<
	string,
	"success" | "warning" | "error"
> = {
	Regular: "success",
	Avariado: "warning",
	"Não possui": "error",
} as const;

export type VehicleConditionsType =
	(typeof VehicleConditionsType)[keyof typeof VehicleConditionsType];

export type OptionQuestion = {
	questionId: number;
	prompt: string;
	optionId: number;
	label: VehicleConditionsType;
};

export type LastSubmitted = {
	id: number;
	createdAt: string; // ISO string
	submittedAt: string; // ISO string
	damageCount: number;
	damageReportFiles: DamageReportFile[]; // plural → array
	optionQuestions: OptionQuestion[];
};

export type ISummary = {
	id: number;
	answerToken: string;
	checklistName: string;
	checklistDescription?: string | null;
	createdAt: string; // ISO string
	expireDate: string; // ISO string
	previousAnswerId: number;
	lastSubmitted: LastSubmitted;
};
export type IAnswerFile = {
	fileKey: string;
	fileUrl: string;
	description: string;
	damages: number[];
};
export type IAnswer = {
	text: string | null;
	number: number | null;
	boolean: boolean | null;
	dateTimeValue: string | null; // ISO string
	selectedOptionIds: number[];
	files: IAnswerFile[];
};

export type QuestionSettings = {
	allowAnswerRecovery: boolean;
	allowDefaultOption: boolean;
	allowDamageReport: boolean;
	minLength: number | null;
	maxLength: number | null;
	minValue: number | null;
	maxValue: number | null;
	dateRangeType: string | null;
	minDate: string | null;
	maxDate: string | null;
	maxFiles: number | null;
	maxFileSizeMB: number | null;
	optionDisplayType: QuestionOptionDisplayType;
	isMultipleSelection: boolean;
};
export type Option = {
	id: number;
	label: string | VehicleConditionsType;
	position: number;
	isDefault: boolean;
	isDamage: boolean;
};
export type ITypes = "Text" | "Number" | "Boolean" | "Date" | "Option" | "File";
export type Question = {
	questionId: number;
	templateQuestionId: number;
	checklistAnswerQuestionId: number;
	prompt: string;
	description?: string | null;
	isRequired: boolean;
	questionType: ITypes;
	position: number;
	settings: QuestionSettings;
	options: Option[];
	answer: IAnswer | null;
};
export type Section = {
	id: number;
	name: string;
	description?: string | null;
	position: number;
	questions: Question[];
};
export type ChecklistAnswer = {
	id: number;
	answerToken: string;
	checklistName: string;
	checklistDescription?: string | null;
	checklistType: string;
	status: "InProgress" | "Completed" | string;
	fuelLevel: number | null;
	odometer: number | null;
	damageCount: number | null;
	createdAt: string;
	updatedAt: string;
	expireDate: string;
	submittedAt: string | null;
	file: string | null;
	inspectorSignatureKey: string | null;
	previousAnswerId: number | null;
	sections: Section[];
};

export type ChecklistAnswerResponse = {
	initializeMode: "NotSelected" | "NewAnswer" | "Reused";
	answerInProgress: boolean;
	summary?: ISummary;
	checklistAnswer: ChecklistAnswer;
	isTrailerOrSemiTrailer: boolean;
};
